import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { ChevronRight, MapPin, IndianRupee, Ruler, Shield, ArrowRight, CheckCircle2, Clock, Home as HomeIcon, Building2, Sparkles } from "lucide-react";
import { LOCATION_SLUGS, TYPE_SLUGS, resolveLocation, resolveType, filterParcels, filterProjects, CATEGORY_COMBOS } from "../lib/propertySlugs";
import JsonLd from "../components/JsonLd";
import InquiryDialog from "../components/InquiryDialog";

const fmtINR = (v) => v >= 10000000 ? `₹${(v/10000000).toFixed(2)} Cr` : v >= 100000 ? `₹${(v/100000).toFixed(1)} L` : `₹${v}`;

function useMeta({ title, description, canonical }) {
  useEffect(() => {
    if (title) document.title = title;
    const setMeta = (name, content, attr = "name") => {
      if (!content) return;
      let m = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!m) {
        m = document.createElement("meta");
        m.setAttribute(attr, name);
        document.head.appendChild(m);
      }
      m.setAttribute("content", content);
    };
    setMeta("description", description);
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:url", canonical, "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);

    // Canonical link
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonical);
    // No cleanup — meta tags stay until next page overrides them.
  }, [title, description, canonical]);
}

export default function PropertiesCategory() {
  const { location: locSlug, type: typeSlug } = useParams();
  const navigate = useNavigate();
  const loc = resolveLocation(locSlug);
  const typ = resolveType(typeSlug);
  const [parcels, setParcels] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inquiryParcel, setInquiryParcel] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [p, pr] = await Promise.all([api.get("/parcels"), api.get("/projects")]);
        setParcels(p.data); setProjects(pr.data);
      } catch { toast.error("Failed to load properties"); }
      finally { setLoading(false); }
    })();
  }, []);

  const validRoute = !!(loc && typ);
  const filteredParcels = useMemo(() => (validRoute ? filterParcels(parcels, loc, typ) : []), [parcels, loc, typ, validRoute]);
  const filteredProjects = useMemo(() => (validRoute ? filterProjects(projects, loc, typ) : []), [projects, loc, typ, validRoute]);
  const totalCount = filteredParcels.length + filteredProjects.length;

  const locLabel = loc?.label || "Unknown location";
  const typLabel = typ?.label || "Properties";
  const title = validRoute
    ? `${typLabel} for Sale in ${locLabel} | AcrePulse`
    : `Category not found | AcrePulse`;
  const description = validRoute
    ? `Explore verified ${typLabel} for sale in ${locLabel}. View prices, total acreage, location maps, and contact seller on AcrePulse.`
    : `Browse verified real-estate categories on AcrePulse.`;
  const canonical = typeof window !== "undefined" ? window.location.href : "";

  useMeta({ title, description, canonical });

  // Aggregate stats for hero
  const totalAcres = filteredParcels.reduce((s, p) => s + (p.size_acres || 0), 0);
  const minPrice = Math.min(
    ...filteredParcels.map((p) => p.price_per_acre * p.size_acres).filter(Boolean),
    ...filteredProjects.map((p) => p.price_min).filter(Boolean),
    Infinity
  );
  const maxPrice = Math.max(
    ...filteredParcels.map((p) => p.price_per_acre * p.size_acres).filter(Boolean),
    ...filteredProjects.map((p) => p.price_max).filter(Boolean),
    0
  );

  const itemListLd = validRoute && totalCount > 0 ? {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `${typLabel} in ${locLabel}`,
    numberOfItems: totalCount,
    itemListElement: [
      ...filteredParcels.slice(0, 25).map((p, i) => ({
        "@type": "ListItem", position: i + 1,
        url: `${window.location.origin}/map?p=${p.id}`,
        name: p.title,
      })),
      ...filteredProjects.slice(0, 25).map((p, i) => ({
        "@type": "ListItem", position: filteredParcels.length + i + 1,
        url: `${window.location.origin}/projects?prj=${p.id}`,
        name: p.project_name,
      })),
    ],
  } : null;

  const breadcrumbLd = validRoute ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${window.location.origin}/` },
      { "@type": "ListItem", position: 2, name: "Properties", item: `${window.location.origin}/properties` },
      { "@type": "ListItem", position: 3, name: locLabel, item: `${window.location.origin}/properties/${locSlug}` },
      { "@type": "ListItem", position: 4, name: typLabel, item: canonical },
    ],
  } : null;

  return (
    <div className="bg-[#0F172A] text-[#F8FAFC]">
      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" data-testid="breadcrumbs" className="mx-auto max-w-[1400px] px-6 pt-6 text-xs text-slate-400">
        <ol className="flex items-center gap-2 flex-wrap">
          <li><Link to="/" className="hover:text-[#10B981]">Home</Link></li>
          <ChevronRight className="h-3 w-3 text-slate-600" />
          <li><Link to="/properties" className="hover:text-[#10B981]">Properties</Link></li>
          <ChevronRight className="h-3 w-3 text-slate-600" />
          <li><Link to={`/properties/${locSlug}/all-properties`} className="hover:text-[#10B981]">{locLabel}</Link></li>
          <ChevronRight className="h-3 w-3 text-slate-600" />
          <li className="text-white">{typLabel}</li>
        </ol>
      </nav>

      {/* Hero */}
      <section className="mx-auto max-w-[1400px] px-6 pt-6 pb-10 border-b border-[#334155]">
        <div className="text-xs uppercase tracking-[0.2em] text-[#10B981]">Programmatic Discovery</div>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter leading-[1.05]">
          {typLabel} for Sale in <span className="text-[#10B981]">{locLabel}</span>
        </h1>
        <p className="mt-3 text-slate-400 text-sm max-w-2xl">{description}</p>

        {validRoute && totalCount > 0 && (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Listings" value={totalCount} />
            {totalAcres > 0 && <Stat label="Total Acreage" value={`${totalAcres.toFixed(1)} Ac`} />}
            {isFinite(minPrice) && <Stat label="Starts From" value={fmtINR(minPrice)} accent />}
            {maxPrice > 0 && <Stat label="Up To" value={fmtINR(maxPrice)} />}
          </div>
        )}
      </section>

      {/* Grid */}
      <section className="mx-auto max-w-[1400px] px-6 py-12">
        {!validRoute && (
          <EmptyState
            title="This category doesn't exist yet"
            body="Try one of the popular categories below or head back to the map."
          />
        )}
        {validRoute && loading && (
          <div className="text-slate-500 text-sm py-10 text-center">Loading listings…</div>
        )}
        {validRoute && !loading && totalCount === 0 && (
          <EmptyState
            title="No properties found in this location yet"
            body="Submit a listing to become the first in this category — or explore related categories below."
            action={
              <Button onClick={() => setInquiryParcel({ id: null, title: `${typLabel} in ${locLabel}` })}
                className="rounded-full bg-[#10B981] hover:bg-[#059669] text-white">
                Submit a listing <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            }
          />
        )}
        {validRoute && !loading && totalCount > 0 && (
          <div>
            {filteredParcels.length > 0 && (
              <>
                <h2 className="font-display text-xl font-semibold flex items-center gap-2"><HomeIcon className="h-4 w-4 text-[#10B981]" /> Land parcels ({filteredParcels.length})</h2>
                <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredParcels.map((p) => <ParcelCard key={p.id} p={p} onInquire={() => setInquiryParcel(p)} />)}
                </div>
              </>
            )}
            {filteredProjects.length > 0 && (
              <>
                <h2 className="mt-14 font-display text-xl font-semibold flex items-center gap-2"><Building2 className="h-4 w-4 text-[#10B981]" /> Builder projects ({filteredProjects.length})</h2>
                <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredProjects.map((p) => <ProjectCard key={p.id} p={p} onOpen={() => navigate(`/projects?prj=${p.id}`)} />)}
                </div>
              </>
            )}
          </div>
        )}
      </section>

      {/* Related categories for internal SEO linking */}
      <section className="mx-auto max-w-[1400px] px-6 pb-16">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Related categories</div>
        <h3 className="mt-2 font-display text-2xl font-semibold tracking-tight">Explore more on AcrePulse</h3>
        <div className="mt-6 flex flex-wrap gap-2">
          {CATEGORY_COMBOS
            .filter(({ locSlug: l, typSlug: t }) => l !== locSlug || t !== typeSlug)
            .slice(0, 30)
            .map((c) => (
              <Link key={`${c.locSlug}-${c.typSlug}`} to={`/properties/${c.locSlug}/${c.typSlug}`}
                className="text-xs px-3 py-1.5 rounded-full border border-[#334155] text-slate-300 hover:border-[#10B981] hover:text-[#10B981] transition-colors">
                {c.label}
              </Link>
            ))}
        </div>
      </section>

      <InquiryDialog open={!!inquiryParcel} onClose={() => setInquiryParcel(null)} parcel={inquiryParcel} />

      {itemListLd && <JsonLd keyId="acrepulse-itemlist-ld" data={itemListLd} />}
      {breadcrumbLd && <JsonLd keyId="acrepulse-breadcrumb-ld" data={breadcrumbLd} />}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-lg border border-[#334155] bg-[#1E293B] p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className={`mt-1 font-display text-xl font-semibold tabular-nums ${accent ? "text-[#10B981]" : "text-white"}`}>{value}</div>
    </div>
  );
}

function EmptyState({ title, body, action }) {
  return (
    <div data-testid="empty-state" className="rounded-xl border border-dashed border-[#334155] bg-[#1E293B] p-10 text-center">
      <Sparkles className="mx-auto h-6 w-6 text-[#10B981]" />
      <h3 className="mt-4 font-display text-xl font-semibold">{title}</h3>
      <p className="mt-2 text-sm text-slate-400 max-w-md mx-auto">{body}</p>
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}

function ParcelCard({ p, onInquire }) {
  return (
    <Card data-testid="cat-parcel-card" className="bg-[#1E293B] border-[#334155] hover:border-[#10B981] transition-colors p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500">{p.corridor_location}</div>
          <h3 className="mt-1 font-display text-lg font-semibold tracking-tight leading-tight">{p.title}</h3>
        </div>
        {p.verified ? (
          <Badge className="bg-[#10B981]/15 text-[#10B981] border-[#10B981]/30"><CheckCircle2 className="h-3 w-3 mr-1" />Verified</Badge>
        ) : (
          <Badge variant="outline" className="border-amber-500/40 text-amber-400"><Clock className="h-3 w-3 mr-1" />Audit</Badge>
        )}
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
        <MetricLite icon={Ruler} label="Size" value={`${p.size_acres} Ac`} />
        <MetricLite icon={MapPin} label="Road" value={`${p.road_width_ft} ft`} />
        <MetricLite icon={Shield} label="Score" value={`${p.legal_score}/10`} />
      </div>
      <div className="mt-4 flex items-center justify-between">
        <div className="text-[#10B981] font-semibold flex items-center"><IndianRupee className="h-3.5 w-3.5" />{fmtINR(p.price_per_acre).replace("₹", "")}<span className="text-slate-500 text-[10px] ml-1">/acre</span></div>
        <Link to={`/map?p=${p.id}`} className="text-[10px] uppercase tracking-[0.2em] text-slate-400 hover:text-[#10B981]">View →</Link>
      </div>
      <Button onClick={onInquire} className="mt-4 w-full rounded-full bg-[#10B981] hover:bg-[#059669] text-white h-9 text-sm">Contact Seller</Button>
    </Card>
  );
}

function ProjectCard({ p, onOpen }) {
  return (
    <Card data-testid="cat-project-card" className="bg-[#1E293B] border-[#334155] hover:border-[#10B981] transition-colors overflow-hidden">
      {p.image_url && <img alt={p.project_name} src={p.image_url} className="h-40 w-full object-cover" />}
      <div className="p-5">
        <div className="flex items-center justify-between">
          <Badge className="bg-[#10B981]/15 text-[#10B981] border-[#10B981]/30">{p.tier}</Badge>
          <span className="text-xs text-slate-500 uppercase tracking-[0.2em]">{p.property_type}</span>
        </div>
        <div className="mt-3 text-xs uppercase tracking-[0.2em] text-slate-500">{p.builder_name}</div>
        <h3 className="mt-1 font-display text-lg font-semibold tracking-tight leading-tight">{p.project_name}</h3>
        <div className="mt-2 text-sm text-slate-400 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{p.location}</div>
        <div className="mt-4 text-[#10B981] font-semibold text-sm">{fmtINR(p.price_min)} – {fmtINR(p.price_max)}</div>
        <Button onClick={onOpen} className="mt-4 w-full rounded-full bg-[#10B981] hover:bg-[#059669] text-white h-9 text-sm">View & Enquire</Button>
      </div>
    </Card>
  );
}

function MetricLite({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-1.5">
      <Icon className="h-3 w-3 text-slate-500 mt-0.5" />
      <div>
        <div className="text-[9px] uppercase tracking-[0.15em] text-slate-500">{label}</div>
        <div className="text-white text-xs font-medium">{value}</div>
      </div>
    </div>
  );
}

// Export slug helpers for the index page
export { LOCATION_SLUGS, TYPE_SLUGS };
